<template>
  <div class="b2b-schedule-page">
    <h1>B2B Account Schedule - Admin Mode</h1>

    <div v-if="authState === 'loading'" class="content-section auth-state">
      <font-awesome-icon icon="spinner" spin />
      <span>Checking access…</span>
    </div>

    <div v-else-if="authState === 'denied'" class="content-section auth-state auth-state--denied">
      <h2>Not authorised</h2>
      <p>This page is restricted to Super Managers (level 25 and above).</p>
      <router-link to="/b2b-schedule" class="back-link">Back to schedule</router-link>
    </div>

    <template v-else>
      <div id="page-b2b-schedule-admin" class="content-section">
        <div class="page-section">
          <div class="page-actions-bar">
            <router-link to="/b2b-schedule" class="back-link">
              <font-awesome-icon icon="arrow-left" class="me-2" />
              Back to schedule
            </router-link>

            <div class="page-actions-right">
              <button
                v-if="groupedAccounts.length > 0"
                type="button"
                class="btn btn-outline-secondary bar-action-btn"
                @click="collapseAll"
              >
                Collapse all
              </button>
              <button
                v-if="groupedAccounts.length > 0"
                type="button"
                class="btn btn-outline-secondary bar-action-btn"
                @click="expandAll"
              >
                Expand all
              </button>

              <div
                class="add-account-area"
                :class="{ 'add-account-area--search-open': showAddSearch }"
              >
            <button
              v-if="!showAddSearch"
              type="button"
              class="btn btn-primary add-account-btn"
              @click="openAddSearch"
            >
              <font-awesome-icon icon="plus" class="me-2" />
              Add account
            </button>

            <div v-else class="add-search-panel">
              <div class="add-search-panel__header">
                <p class="add-search-panel__eyebrow">Add account</p>
                <button
                  type="button"
                  class="add-search-panel__close"
                  aria-label="Close search"
                  @click="closeAddSearch"
                >
                  ×
                </button>
              </div>
              <FormKit
                v-model="selectedCustomerId"
                type="autocomplete"
                label="Search customer"
                label-class="add-search-label"
                outer-class="add-search-field"
                placeholder="Name, account code, or customer ID"
                :options="searchCustomers"
                searchable
                open-on-click
                clear-search-on-open
              />
            </div>
          </div>
        </div>
        </div>

        <div class="schedule-scroll-body">
    <div
      v-if="feedbackMessage"
      class="alert"
      :class="feedbackType === 'error' ? 'alert-danger' : 'alert-success'"
      role="alert"
    >
      {{ feedbackMessage }}
    </div>

    <div v-if="loadingAccounts" class="empty-state">
      <font-awesome-icon icon="spinner" spin class="empty-state__icon" />
      <p>Loading schedule accounts…</p>
    </div>

    <div v-else-if="groupedAccounts.length === 0" class="empty-state">
      <font-awesome-icon icon="calendar-plus" class="empty-state__icon" />
      <h2>No accounts scheduled yet</h2>
      <p>Add your first B2B account using the button above.</p>
    </div>

    <div v-else class="schedule-groups">
      <section
        v-for="group in groupedAccounts"
        :key="group.name"
        class="round-group-card"
      >
        <button
          type="button"
          class="group-header-btn"
          :style="getGroupHeaderStyle(group.name)"
          :aria-expanded="!isGroupCollapsed(group.name)"
          @click="toggleGroup(group.name)"
        >
          <div class="group-header-btn__main">
            <font-awesome-icon
              :icon="isGroupCollapsed(group.name) ? 'chevron-right' : 'chevron-down'"
              class="collapse-icon"
            />
            <span class="round-title">{{ group.name }}</span>
          </div>
          <span class="account-count">
            {{ group.accounts.length }} account{{ group.accounts.length === 1 ? '' : 's' }}
          </span>
        </button>

        <div v-show="!isGroupCollapsed(group.name)" class="schedule-table-wrap schedule-table-wrap--group">
          <table class="schedule-table align-middle">
            <colgroup>
              <col class="col-account-id" />
              <col class="col-name" />
              <col class="col-account-code" />
              <col class="col-days" />
              <col class="col-branding" />
              <col class="col-active" />
              <col class="col-delete" />
            </colgroup>
            <thead>
              <tr>
                <th>Account ID</th>
                <th>Name</th>
                <th>Account code</th>
                <th>Scheduled days</th>
                <th>Branding</th>
                <th class="th-active">Active</th>
                <th class="th-actions" aria-label="Actions"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="account in group.accounts"
                :key="account.b2b_schedule_id"
                :class="{ 'account-row--inactive': Number(account.active) === 0 }"
              >
                <td class="account-id">{{ account.customer_id }}</td>
                <td>
                  <a
                    :href="getCustomerEditUrl(account.customer_id)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="customer-link"
                  >
                    {{ formatFullName(account.firstname, account.surname) }}
                  </a>
                </td>
                <td class="account-code-cell">{{ account.accountcode }}</td>
                <td>
                  <div class="day-pills">
                    <button
                      v-for="day in dayOptions"
                      :key="`${account.b2b_schedule_id}-${day.key}`"
                      type="button"
                      class="day-pill"
                      :class="{
                        'day-pill--active': Number(account.days?.[day.key]) === 1,
                        'day-pill--saving': isDaySaving(account.b2b_schedule_id, day.key),
                      }"
                      :disabled="isDaySaving(account.b2b_schedule_id, day.key)"
                      @click="toggleAccountDay(account, day.key)"
                    >
                      {{ day.label }}
                    </button>
                  </div>
                </td>
                <td>
                  <div class="branding-cell">
                    <div class="logo-wrap">
                      <img
                        v-if="account.logopath_publicURL"
                        :src="account.logopath_publicURL"
                        :alt="formatFullName(account.firstname, account.surname)"
                        class="customer-logo"
                      />
                      <div v-else class="logo-placeholder">
                        <font-awesome-icon icon="building" />
                      </div>
                    </div>
                    <div class="colour-stripes" aria-label="Customer colours">
                      <span
                        v-for="colourKey in colourKeys"
                        :key="colourKey"
                        class="colour-stripe"
                        :style="{ backgroundColor: toRgb(account[colourKey]) }"
                      />
                    </div>
                  </div>
                </td>
                <td class="td-control active-cell">
                  <div class="form-check form-switch account-active-switch">
                    <input
                      :id="`active-${account.b2b_schedule_id}`"
                      class="form-check-input"
                      type="checkbox"
                      role="switch"
                      :checked="Number(account.active) === 1"
                      :disabled="isActiveSaving(account.b2b_schedule_id)"
                      @change="toggleAccountActive(account)"
                    />
                    <label
                      class="form-check-label visually-hidden"
                      :for="`active-${account.b2b_schedule_id}`"
                    >
                      {{ Number(account.active) === 1 ? 'Disable' : 'Enable' }} account
                    </label>
                  </div>
                </td>
                <td class="td-control delete-cell">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-danger delete-account-btn"
                    :disabled="isDeleting(account.b2b_schedule_id)"
                    title="Remove from schedule"
                    @click="deleteAccount(account)"
                  >
                    <font-awesome-icon
                      :icon="isDeleting(account.b2b_schedule_id) ? 'spinner' : 'trash'"
                      :spin="isDeleting(account.b2b_schedule_id)"
                    />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
        </div>
      </div>
    </div>

    <div v-if="showAddModal" class="modal d-block"
      tabindex="-1"
      role="dialog"
      @click.self="closeAddModal"
    >
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content add-modal">
          <div class="modal-header">
            <div>
              <p class="modal-eyebrow">Add B2B account</p>
              <h5 class="modal-title">
                {{ formatFullName(modalCustomer.firstname, modalCustomer.surname) }}
              </h5>
            </div>
            <button type="button" class="btn-close" aria-label="Close" @click="closeAddModal" />
          </div>

          <div class="modal-body">
            <div class="detail-grid">
              <div class="detail-card">
                <span class="detail-label">First name</span>
                <strong>{{ modalCustomer.firstname }}</strong>
              </div>
              <div class="detail-card">
                <span class="detail-label">Last name</span>
                <strong>{{ modalCustomer.surname }}</strong>
              </div>
              <div class="detail-card detail-card--wide">
                <span class="detail-label">Account code</span>
                <strong>{{ modalCustomer.accountcode }}</strong>
              </div>
            </div>

            <div class="days-section">
              <div class="days-section__header">
                <h6>Visit days</h6>
                <span class="days-hint">Optional — you can add without selecting any days.</span>
              </div>
              <div class="day-toggle-grid">
                <button
                  v-for="day in dayOptions"
                  :key="day.key"
                  type="button"
                  class="day-toggle"
                  :class="{ 'day-toggle--active': Number(modalDays[day.key]) === 1 }"
                  @click="toggleModalDay(day.key)"
                >
                  {{ day.label }}
                </button>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" @click="closeAddModal">
              Cancel
            </button>
            <button type="button" class="btn btn-primary" @click="confirmAddAccount">
              Add account
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showAddModal" class="modal-backdrop show" />
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@/assets/css/b2b-page-fit.css';
import { useB2bPageFit } from '@/composables/useB2bPageFit';

import B2BScheduleService from '@/services/B2BScheduleService';
import CustomerColoursDataService from '@/services/CustomerColoursDataService';
import SessionService from '@/services/SessionService';
import {
  DAY_OPTIONS,
  emptyDays,
  formatFullName,
  formatCustomerSearchLabel,
  getCustomerEditUrl,
  getRoundGroupTheme,
  groupAccounts,
  parseNameFromAssemblyText,
  parseRoundGroup,
} from '@/utils/b2bScheduleHelpers';

const SUPER_MANAGER_LEVEL = 25;

const authState = ref('loading');
const accounts = ref([]);
const loadingAccounts = ref(false);
const collapsedGroups = ref({});
const savingDayKeys = ref({});
const savingActiveIds = ref({});
const deletingIds = ref({});

const showAddSearch = ref(false);
const showAddModal = ref(false);
const selectedCustomerId = ref('');
const customerSearchCache = ref({});
const modalCustomer = ref({
  customerid: null,
  firstname: '',
  surname: '',
  accountcode: '',
});
const modalDays = ref(emptyDays());
const feedbackMessage = ref('');
const feedbackType = ref('success');

const dayOptions = DAY_OPTIONS;
const colourKeys = ['rgb_colour1', 'rgb_colour2', 'rgb_colour3'];

const groupedAccounts = computed(() => groupAccounts(accounts.value));

function getGroupHeaderStyle(groupName) {
  const theme = getRoundGroupTheme(groupName);

  return {
    backgroundColor: theme.bg,
    borderColor: theme.border,
    color: theme.text,
  };
}

function isGroupCollapsed(groupName) {
  return collapsedGroups.value[groupName] === true;
}

function toggleGroup(groupName) {
  collapsedGroups.value = {
    ...collapsedGroups.value,
    [groupName]: !isGroupCollapsed(groupName),
  };
}

function expandAll() {
  collapsedGroups.value = {};
}

function collapseAll() {
  const collapsed = {};
  groupedAccounts.value.forEach((group) => {
    collapsed[group.name] = true;
  });
  collapsedGroups.value = collapsed;
}

function daySavingKey(scheduleId, dayKey) {
  return `${scheduleId}:${dayKey}`;
}

function isDaySaving(scheduleId, dayKey) {
  return savingDayKeys.value[daySavingKey(scheduleId, dayKey)] === true;
}

function setDaySaving(scheduleId, dayKey, saving) {
  const key = daySavingKey(scheduleId, dayKey);
  if (saving) {
    savingDayKeys.value[key] = true;
  } else {
    delete savingDayKeys.value[key];
  }
}

function isActiveSaving(scheduleId) {
  return savingActiveIds.value[scheduleId] === true;
}

function isDeleting(scheduleId) {
  return deletingIds.value[scheduleId] === true;
}

function normalizeAccount(account) {
  return {
    ...account,
    days: { ...account.days },
  };
}

async function enrichBranding(accountList) {
  try {
    const response = await CustomerColoursDataService.getAll();
    const settings = response.data?.customers || [];
    const settingsByCustomerId = {};

    settings.forEach((setting) => {
      settingsByCustomerId[setting.customer_id] = setting;
    });

    accountList.forEach((account) => {
      const setting = settingsByCustomerId[account.customer_id];
      if (!setting) {
        return;
      }

      account.logopath = setting.logopath || account.logopath || '';
      account.logopath_publicURL = setting.logopath_publicURL || '';
      account.rgb_colour1 = setting.rgb_colour1 || account.rgb_colour1 || '';
      account.rgb_colour2 = setting.rgb_colour2 || account.rgb_colour2 || '';
      account.rgb_colour3 = setting.rgb_colour3 || account.rgb_colour3 || '';
    });
  } catch (error) {
    console.error(error);
  }
}

async function loadAccounts() {
  loadingAccounts.value = true;

  try {
    const response = await B2BScheduleService.getAccounts();
    const loadedAccounts = (response.data?.data?.accounts || []).map(normalizeAccount);
    await enrichBranding(loadedAccounts);
    accounts.value = loadedAccounts;
  } catch (error) {
    console.error(error);
    if (error.response?.status === 403) {
      authState.value = 'denied';
    } else {
      setFeedback('Unable to load B2B schedule accounts.', 'error');
    }
  } finally {
    loadingAccounts.value = false;
  }
}

async function checkAccess() {
  try {
    const response = await SessionService.getSessionDetails();
    const userLevel = Number(response.data?.data?.user?.userlevel || 0);

    if (userLevel >= SUPER_MANAGER_LEVEL) {
      authState.value = 'authorized';
      await loadAccounts();
    } else {
      authState.value = 'denied';
    }
  } catch (error) {
    console.error(error);
    authState.value = 'denied';
  }
}

async function toggleAccountDay(account, dayKey) {
  if (!account?.b2b_schedule_id || isDaySaving(account.b2b_schedule_id, dayKey)) {
    return;
  }

  const previousValue = Number(account.days?.[dayKey]) === 1 ? 1 : 0;
  const nextValue = previousValue === 1 ? 0 : 1;

  account.days[dayKey] = nextValue;
  setDaySaving(account.b2b_schedule_id, dayKey, true);

  try {
    await B2BScheduleService.toggleDay(account.b2b_schedule_id, dayKey);
  } catch (error) {
    console.error(error);
    account.days[dayKey] = previousValue;
    if (error.response?.status === 403) {
      authState.value = 'denied';
    } else {
      setFeedback('Failed to update visit day.', 'error');
    }
  } finally {
    setDaySaving(account.b2b_schedule_id, dayKey, false);
  }
}

async function toggleAccountActive(account) {
  if (!account?.b2b_schedule_id || isActiveSaving(account.b2b_schedule_id)) {
    return;
  }

  const previousValue = Number(account.active) === 1 ? 1 : 0;
  const nextValue = previousValue === 1 ? 0 : 1;

  account.active = nextValue;
  savingActiveIds.value[account.b2b_schedule_id] = true;

  try {
    await B2BScheduleService.toggleActive(account.b2b_schedule_id);
  } catch (error) {
    console.error(error);
    account.active = previousValue;
    if (error.response?.status === 403) {
      authState.value = 'denied';
    } else {
      setFeedback('Failed to update account status.', 'error');
    }
  } finally {
    delete savingActiveIds.value[account.b2b_schedule_id];
  }
}

async function deleteAccount(account) {
  if (!account?.b2b_schedule_id || isDeleting(account.b2b_schedule_id)) {
    return;
  }

  const accountName = formatFullName(account.firstname, account.surname);
  const confirmed = window.confirm(
    `Remove ${accountName} from the B2B schedule?\n\nThis can be re-added later via Add account.`,
  );

  if (!confirmed) {
    return;
  }

  deletingIds.value[account.b2b_schedule_id] = true;

  try {
    await B2BScheduleService.deleteAccount(account.b2b_schedule_id);
    accounts.value = accounts.value.filter(
      (row) => row.b2b_schedule_id !== account.b2b_schedule_id,
    );
    setFeedback(`Removed ${accountName} from schedule.`);
  } catch (error) {
    console.error(error);
    if (error.response?.status === 403) {
      authState.value = 'denied';
    } else {
      setFeedback('Failed to remove account from schedule.', 'error');
    }
  } finally {
    delete deletingIds.value[account.b2b_schedule_id];
  }
}

function toRgb(value) {
  if (!value) {
    return '#d7e2ea';
  }

  return `rgb(${value})`;
}

function setFeedback(message, type = 'success') {
  feedbackMessage.value = message;
  feedbackType.value = type;

  window.setTimeout(() => {
    feedbackMessage.value = '';
  }, 4500);
}

function openAddSearch() {
  showAddSearch.value = true;
  selectedCustomerId.value = '';
}

function closeAddSearch() {
  showAddSearch.value = false;
  selectedCustomerId.value = '';
}

async function searchCustomers({ search }) {
  try {
    const response = await B2BScheduleService.findByCustomer(search);
    const customers = response.data?.customers || [];

    customers.forEach((customer) => {
      customerSearchCache.value[customer.id] = customer;
    });

    return customers.map((customer) => ({
      label: formatCustomerSearchLabel(customer),
      value: customer.id,
    }));
  } catch (error) {
    console.error(error);
    return [];
  }
}

async function onCustomerSelected(customerId) {
  if (!customerId) {
    return;
  }

  const entry = customerSearchCache.value[customerId];
  if (!entry) {
    return;
  }

  let firstname = entry.firstname || '';
  let surname = entry.surname || '';

  if (!firstname) {
    try {
      const response = await B2BScheduleService.getCustomerDetails(entry.accountcode);
      const match = (response.data?.data?.customers || []).find(
        (customer) => Number(customer.customerid) === Number(customerId),
      );

      if (match) {
        firstname = match.firstname;
        surname = match.surname;
      }
    } catch (error) {
      console.error(error);
    }
  }

  if (!firstname && entry.text) {
    const parsed = parseNameFromAssemblyText(entry.text, customerId);
    firstname = parsed.firstname;
    surname = parsed.surname;
  }

  modalCustomer.value = {
    customerid: entry.id,
    firstname,
    surname,
    accountcode: entry.accountcode,
  };
  modalDays.value = emptyDays();
  showAddModal.value = true;
  closeAddSearch();
  selectedCustomerId.value = '';
}

watch(selectedCustomerId, (customerId) => {
  onCustomerSelected(customerId);
});

function closeAddModal() {
  showAddModal.value = false;
  modalCustomer.value = {
    customerid: null,
    firstname: '',
    surname: '',
    accountcode: '',
  };
  modalDays.value = emptyDays();
}

function toggleModalDay(dayKey) {
  modalDays.value[dayKey] = Number(modalDays.value[dayKey]) === 1 ? 0 : 1;
}

async function confirmAddAccount() {
  const customer = modalCustomer.value;
  if (!customer?.customerid) {
    return;
  }

  const roundGroup = parseRoundGroup(customer.firstname);

  try {
    const response = await B2BScheduleService.createAccount({
      customer_id: Number(customer.customerid),
      days: { ...modalDays.value },
    });

    if (response.data?.data?.accounts) {
      const loadedAccounts = response.data.data.accounts.map(normalizeAccount);
      await enrichBranding(loadedAccounts);
      accounts.value = loadedAccounts;
    } else {
      await loadAccounts();
    }

    closeAddModal();
    setFeedback(`Added to group ${roundGroup}.`);
  } catch (error) {
    console.error(error);
    if (error.response?.status === 403) {
      authState.value = 'denied';
      closeAddModal();
      return;
    }

    const message = error.response?.data?.message || 'Unable to add account to schedule.';
    setFeedback(message, 'error');
  }
}

useB2bPageFit();

onMounted(() => {
  checkAccess();
});
</script>

<style scoped>
.page-actions-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  margin-bottom: 1rem;
  flex-shrink: 0;
}

.page-actions-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.bar-action-btn {
  font-size: 0.82rem;
  font-weight: 600;
  white-space: nowrap;
  padding: 0.3rem 0.65rem;
}

.back-link {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  color: #0f4c81;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
}

.back-link:hover {
  color: #1f7bb6;
}

.auth-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  min-height: 280px;
  text-align: center;
  color: #47657d;
}

.auth-state--denied h2 {
  margin: 0;
  color: #17324d;
}

.schedule-table-wrap {
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(15, 76, 129, 0.07);
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(31, 123, 182, 0.05);
  overflow-x: auto;
}

.schedule-groups {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.round-group-card {
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(15, 76, 129, 0.07);
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(31, 123, 182, 0.05);
  overflow: hidden;
}

.group-header-btn {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  width: 100%;
  padding: 0.65rem 0.9rem;
  border: 0;
  cursor: pointer;
  text-align: left;
  transition: filter 0.15s ease;
}

.group-header-btn:hover {
  filter: brightness(0.98);
}

.group-header-btn__main {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  min-width: 0;
}

.schedule-table-wrap--group {
  border: 0;
  border-radius: 0;
  box-shadow: none;
  padding: 0.35rem 0.75rem 0.75rem;
}

.schedule-table thead {
  border: 0;
}

.schedule-table thead tr {
  border: 0;
}

.schedule-table {
  width: 100%;
  table-layout: fixed;
  margin: 0;
  border-collapse: collapse;
  border-spacing: 0;
  background: #fff;
}

.col-account-id { width: 8%; }
.col-name { width: 18%; }
.col-account-code { width: 12%; }
.col-days { width: 30%; }
.col-branding { width: 16%; }
.col-active { width: 4.5rem; }
.col-delete { width: 3.25rem; }

.empty-state {
  padding: 2rem 1.25rem;
  text-align: center;
}

.empty-state__icon {
  font-size: 1.75rem;
  color: #1f7bb6;
  margin-bottom: 0.65rem;
}

.collapse-icon {
  width: 0.85rem;
  flex-shrink: 0;
  opacity: 0.85;
}

.round-title {
  font-size: 0.98rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.account-count {
  font-size: 0.82rem;
  font-weight: 600;
  opacity: 0.9;
  white-space: nowrap;
}

.add-account-area {
  flex-shrink: 0;
  margin-left: auto;
  max-width: 460px;
}

.add-account-area--search-open {
  width: min(100%, 460px);
  min-width: 380px;
}

.add-account-btn {
  font-weight: 600;
  padding: 0.65rem 1rem;
  border: 0;
  white-space: nowrap;
  box-shadow: 0 8px 20px rgba(31, 123, 182, 0.16);
}

.add-search-panel {
  width: 100%;
  min-width: 380px;
  max-width: 460px;
  padding: 1rem 1.1rem;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 12px 32px rgba(15, 76, 129, 0.1);
}

.add-search-panel__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.add-search-panel__eyebrow {
  margin: 0;
  font-size: 0.82rem;
  font-weight: 700;
  color: #5a7185;
}

.add-search-panel__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: #f1f5f9;
  color: #47657d;
  font-size: 1.25rem;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.add-search-panel__close:hover {
  background: #e2e8f0;
  color: #17324d;
}

.add-search-label {
  color: #17324d !important;
  font-weight: 600 !important;
  font-size: 0.88rem !important;
}

.add-search-panel :deep(.formkit-outer) {
  margin-bottom: 0;
}

.add-search-panel :deep(.formkit-input) {
  border-radius: 10px;
  border-color: #d7e2ea;
  padding: 0.55rem 0.75rem;
  font-size: 0.9rem;
}

.add-search-panel :deep(.formkit-input:focus) {
  border-color: #1f7bb6;
  box-shadow: 0 0 0 3px rgba(31, 123, 182, 0.12);
}

.add-search-panel :deep([data-type='autocomplete'] .formkit-dropdown) {
  border-radius: 10px;
  border-color: #e2e8f0;
  box-shadow: 0 10px 28px rgba(15, 76, 129, 0.12);
  overflow: hidden;
}

.add-search-panel :deep([data-type='autocomplete'] .formkit-listitem) {
  padding-left: 0.75rem !important;
}

.add-search-panel :deep([data-type='autocomplete'] .formkit-listitem .formkit-option) {
  display: block;
  padding: 0.55rem 0;
  font-size: 0.88rem;
  color: #17324d;
}

.add-search-panel :deep([data-type='autocomplete'] .formkit-selected-icon) {
  display: none !important;
}

.add-search-panel :deep([data-type='autocomplete'] .formkit-listitem[data-is-active='true']) {
  background: #eef6fb !important;
  color: #0f4c81;
}

.add-search-panel :deep([data-type='autocomplete'] .formkit-listitem[aria-selected='true']) {
  background: #e3f2fa !important;
  color: #0f4c81;
}

.add-search-panel :deep([data-type='autocomplete'] .formkit-listitem:hover) {
  background: #f4f9fc;
}

.schedule-table thead th {
  background: transparent;
  color: #6b8296;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border: 0;
  border-bottom: 1px solid #e8eef3;
  padding: 0.5rem 0.75rem 0.6rem;
  white-space: nowrap;
  text-align: left;
  vertical-align: bottom;
}

.th-active,
.td-control.active-cell {
  width: 4.5rem;
  min-width: 4.5rem;
  max-width: 4.5rem;
  text-align: left;
  vertical-align: middle;
}

.th-actions,
.td-control.delete-cell {
  width: 3.25rem;
  min-width: 3.25rem;
  max-width: 3.25rem;
  text-align: center;
  vertical-align: middle;
}

.schedule-table tbody td {
  border: 0;
  border-bottom: 1px solid #f3f6f9;
  vertical-align: middle;
  padding: 0.55rem 0.75rem;
  font-size: 0.88rem;
  text-align: left;
}

.schedule-table tbody tr:last-child td {
  border-bottom: 0;
}

.account-id {
  font-weight: 700;
  color: #17324d;
  width: 90px;
}

.customer-link {
  color: #0f4c81;
  font-weight: 600;
  text-decoration: none;
}

.customer-link:hover {
  color: #1f7bb6;
  text-decoration: underline;
}

.account-code-cell {
  color: #4f6578;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.84rem;
  white-space: nowrap;
}

.account-row--inactive td {
  opacity: 0.55;
}

.account-row--inactive .account-active-switch {
  opacity: 1;
}

.account-active-switch {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  margin: 0;
  min-height: 1.5rem;
  padding: 0;
}

.account-active-switch .form-check-input {
  margin: 0;
  cursor: pointer;
  float: none;
}

.delete-account-btn {
  padding: 0.2rem 0.45rem;
  line-height: 1;
}

.branding-cell {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.logo-wrap,
.logo-placeholder {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
}

.customer-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #fff;
}

.logo-placeholder {
  display: grid;
  place-items: center;
  background: #edf2f6;
  color: #6d8498;
  font-size: 0.85rem;
}

.colour-stripes {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.colour-stripe {
  width: 64px;
  height: 8px;
  border-radius: 999px;
  box-shadow: inset 0 0 0 1px rgba(15, 76, 129, 0.06);
}

.add-modal {
  border: 0;
  border-radius: 18px;
  overflow: hidden;
}

.modal-eyebrow {
  margin: 0 0 0.2rem;
  font-size: 0.78rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6d8498;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
}

.detail-card {
  padding: 0.9rem 1rem;
  border-radius: 12px;
  background: #f7fbfd;
}

.detail-card--wide {
  grid-column: 1 / -1;
}

.detail-label {
  display: block;
  margin-bottom: 0.25rem;
  color: #6d8498;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.days-section {
  margin-top: 1.25rem;
}

.days-section__header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: baseline;
  margin-bottom: 0.75rem;
}

.days-section__header h6 {
  margin: 0;
  font-size: 1rem;
  color: #17324d;
}

.days-hint {
  color: #6d8498;
  font-size: 0.85rem;
}

.day-toggle-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0.55rem;
}

.day-toggle {
  border: 1px solid #d7e2ea;
  background: #fff;
  color: #47657d;
  border-radius: 12px;
  padding: 0.7rem 0.35rem;
  font-weight: 700;
  transition: all 0.18s ease;
}

.day-toggle--active {
  background: linear-gradient(135deg, #0f4c81, #1f7bb6);
  border-color: transparent;
  color: #fff;
  box-shadow: 0 10px 18px rgba(31, 123, 182, 0.18);
}

@media (max-width: 900px) {
  .page-actions-bar {
    flex-wrap: wrap;
    align-items: flex-start;
  }

  .add-account-area--search-open {
    width: 100%;
    min-width: 0;
  }

  .add-search-panel {
    width: 100%;
  }

  .days-section__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .day-toggle-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
</style>
